import { CSSProperties, useContext, useState } from "react";
import { Drawer, List, ListItem } from "@material-ui/core";
import { Link, useHistory, useLocation } from "react-router-dom";
import { NavigationItem } from "./Navbar";
import SubscriptionStatusIndicator from "./Components/SubscriptionStatusIndicator";
import useBrand from 'Hooks/useBrand';
import { UpgradeModalContext } from "Hooks/UpgradeModalContext";
import Colors from "modules/Colors";
import useUser from "Hooks/useUser";
import { clsx, makeKey } from "modules/Utils";
import Icon from "elements/Icon";
import useMaestroChat from "Hooks/useMaestroChat";
import MobileHeader from "components/MobileHeader";
import ButtonSymphony from "elements/ButtonSymphony";
import { getWebsiteBuilderPath } from "Hooks/useWebsiteBuilder";
import useCampaignBuilder from "Hooks/useCampaignBuilder";
import useWebsiteBuilder from "Hooks/useWebsiteBuilder";
import CreateNewWebsiteModalV2 from "components/shareable/CreateNewWebsiteModalV2";

const GRAY_MENU_COLOR = "#888888";
const NAVIGATION_FONT_SIZE = 11;

const sty: Record<string, CSSProperties> = {
    iconContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: 53,
        flexShrink: 0,
        gap: 1
    },
    iconDividerSelected: {
        marginTop: 1,
        width: 24,
        height: 1.5,
        backgroundColor: Colors.purpleLight,
        borderRadius: 100,
        transition: 'all 0.2s ease'
    },
    iconDividerBlank: {
        marginTop: 1,
        width: 24,
        height: 1.5,
        backgroundColor: Colors.purpleDark,
        borderRadius: 100
    },
    bottomNavigationContainer1: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        backgroundColor: Colors.purpleDark,
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 24,
        paddingBottom: 24,
        height: 40,
        borderBottom: `1px solid ${Colors.greyDark}`,
    },
    // Toolkit drawer styles - matching Material-UI Drawer approach
    toolkitDrawerContent: {
        backgroundColor: Colors.white,
        paddingTop: 24,
        paddingBottom: 32,
        paddingLeft: 20,
        paddingRight: 20,
        minHeight: 200,
    },
    toolkitPopupHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 24,
    },
    toolkitPopupTitle: {
        fontSize: 20,
        fontWeight: 600,
        color: Colors.black,
    },
    toolkitPopupGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 16,
    },
    toolkitPopupCard: {
        padding: 20,
        borderRadius: 16,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        border: `1px solid ${Colors.greyLight}`,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    },
    toolkitPopupCardIcon: {
        marginBottom: 12,
    },
    toolkitPopupCardTitle: {
        fontSize: 16,
        fontWeight: 600,
        color: Colors.black,
        marginBottom: 4,
    },
    toolkitPopupCardDescription: {
        fontSize: 12,
        color: Colors.greyDark,
        lineHeight: 1.4,
    },
    toolkitQuickActions: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        marginTop: 24,
    },
    toolkitQuickActionButton: {
        width: '100%',
    },
};

const getCurrentlySelectedPage = (
    location: {
        pathname: string;
    },
    navigationItems: NavigationItem[]
): NavigationItem | undefined => {
    const pathname = location.pathname;
    // first search the main HREFs, then the alternate HREFs to see if it matches
    const selectedPage = navigationItems.find((navItem: NavigationItem) => {
        const {
            href: navItemHref,
            alternateHrefs,
            // name
        } = navItem;

        if (navItemHref === pathname) {
            return true
        }

        if (alternateHrefs && alternateHrefs.indexOf(pathname) > -1) {
            return true
        }

        return false
    });

    return selectedPage;
}

interface MobileNavigationProps {
    className: string;
    navigationItems: NavigationItem[];
    isPublic?: boolean;
}

const MobileNavigation = ({
    className,
    navigationItems,
    isPublic = false
}: MobileNavigationProps) => {
    const history = useHistory()
    const location = useLocation();
    const {
        isProBrand, // Will be part of getShouldShowNewUserUpgradePrompt's logic
        currentBrand,
        isBrandAdmin,
        getShouldShowNewUserUpgradePrompt // New selector
    } = useBrand()
    const { proFreeTrialRedeemed, isSymphonyAdmin } = useUser()
    const {
        setShowCreateNewCampaignModal
    } = useCampaignBuilder();
    const {
        setShowCreateNewWebsiteModalV2
    } = useWebsiteBuilder();
    const { handleOpenModal } = useContext(UpgradeModalContext);

    const {
        isChatOpen,
        setIsChatOpen,
        inChatMode
    } = useMaestroChat()

    const websiteBuilderPath = getWebsiteBuilderPath(currentBrand?.slug || '')

    let freeTrialOver = !proFreeTrialRedeemed()
    // location used to check if item selected
    const currentNavigationItem = getCurrentlySelectedPage(location, navigationItems);

    // to handle opening / closing mobile drawer
    const [drawerOpen, setOpenDrawer] = useState<boolean>(false)
    // Add toolkit popup state
    const [toolkitPopupOpen, setToolkitPopupOpen] = useState<boolean>(false)

    const [value, setValue] = useState(0);

    // Filter navigation items for the BottomNavigation bar
    const bottomNavigationItems = navigationItems
        .filter((navItem: NavigationItem) => navItem.showOnMobileMainMenu)
        .filter((navItem: NavigationItem) => {
            if (isPublic) {
                // On public routes, only show items marked as public or always available
                return navItem.isPublic || navItem.isAvailableAlways;
            } else {
                // On authenticated routes, exclude items that are public-only, unless they're always available
                return !navItem.isPublic || navItem.isAvailableAlways;
            }
        })

    // Filter navigation items for the Drawer
    const drawerNavigationItems = navigationItems
        .filter((navItem: NavigationItem) => !navItem.showOnMobileMainMenu)
        .filter((navItem: NavigationItem) => {
            if (isPublic) {
                // On public routes, only show items marked as public or always available
                return navItem.isPublic || navItem.isAvailableAlways;
            } else {
                // On authenticated routes, exclude items that are public-only, unless they're always available
                return !navItem.isPublic || navItem.isAvailableAlways;
            }
        })
        // Filter out toolkit items from drawer since they're now in the toolkit popup
        .filter((navItem: NavigationItem) => 
            navItem.name !== 'Marketing' && 
            navItem.name !== 'Website' && 
            navItem.name !== 'Dashboard' && 
            navItem.name !== 'Fans'
        )

    const showUpgradeToProButton = () => {
        if (false) {
            return (
                <div
                    className="bottomNavigationContainer3"
                    style={{
                        backgroundColor: Colors.purpleDark,
                        height: 56,
                        paddingLeft: 12,
                        paddingRight: 12,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderBottom: `1px solid ${Colors.greyDark}`,
                    }}
                >
                    <ButtonSymphony
                        className="bottomNavigationButton"
                        onClick={() => setIsChatOpen(true)}
                        variant="primary"
                        size="small"
                    >
                        Open Maestro Chat
                    </ButtonSymphony>
                </div>
            )
        }

        if (isProBrand) {
            return null
        }
    }

    const renderBottomNavigatorContainer1 = () => {
        // Remove from mobile since these actions are now in the Toolkit popup
        return null
    }

    return (
        <div className={clsx('mobileNavigationComponent', className)}>
            <div
                className="mobileNavigationInner"
                style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 99,
                    transform: inChatMode ? 'translateY(100%)' : 'translateY(0)',
                    transition: 'transform 0.3s ease'
                }}
            >
                {showUpgradeToProButton()}

                {renderBottomNavigatorContainer1()}

                <div
                    className="mobileNavigationBottomNavigation"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: Colors.purpleDark,
                        height: 56,
                        paddingLeft: 12,
                        paddingRight: 12
                    }}
                >
                    {bottomNavigationItems.map((item: NavigationItem, index: number) => {
                        // Special logic for Toolkit selection - show as selected when on any toolkit page
                        let isSelected = false;
                        if (item.toolkitItems) {
                            // Check if current path matches any toolkit item's href
                            const toolkitPaths = item.toolkitItems.map(toolkitItem => toolkitItem.href);
                            isSelected = toolkitPaths.some(path => location.pathname.startsWith(path));
                        } else {
                            // Normal selection logic for other items
                            isSelected = Boolean(currentNavigationItem && currentNavigationItem.name === item.name);
                        }

                        // const IconComponent = item.iconComponent;
                        const color = isSelected ? 'white' : GRAY_MENU_COLOR;

                        return (
                            <div
                                key={makeKey(item.name, index)}
                                className="iconContainer1"
                                style={sty.iconContainer}
                                onClick={() => {
                                    const isPaywalledFeature = item.href === '/marketing' || item.href === '/website' || item.href === '/fans';
                                    if (isPaywalledFeature && getShouldShowNewUserUpgradePrompt()) {
                                        let source = "MobileNavbar";
                                        if (item.href === '/marketing') source = "MobileNavbar - Marketing Tab";
                                        else if (item.href === '/website') source = "MobileNavbar - Website Tab";
                                        else if (item.href === '/fans') source = "MobileNavbar - Fans Tab";
                                        handleOpenModal({ source });
                                        setOpenDrawer(false); // Close drawer if it was open for some reason
                                        return;
                                    }
                                    setOpenDrawer(false)
                                    // Handle Toolkit special case - check for toolkitItems instead of name
                                    if (item.toolkitItems) {
                                        setToolkitPopupOpen(true)
                                    } else {
                                        history.push(item.href!)
                                    }
                                }}
                            >
                                <Icon
                                    className="mobileNavigationBottomNavigationActionIcon"
                                    icon={item.icon || ''}
                                    color={color}
                                />
                                <div
                                    className="mobileNavigationBottomNavigationActionText"
                                    style={{
                                        color: color,
                                        fontSize: NAVIGATION_FONT_SIZE,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {item.name}
                                </div>
                                <div style={isSelected ? sty.iconDividerSelected : sty.iconDividerBlank} />
                            </div>
                        );
                    })}
                    {!isPublic &&
                        <div
                            className="iconContainer2"
                            onClick={() => setOpenDrawer(true)}
                            style={sty.iconContainer}
                        >
                            <Icon
                                className="mobileNavigationBottomNavigationActionIcon"
                                icon="gear"
                                color={GRAY_MENU_COLOR}
                            />
                            <div style={{ color: GRAY_MENU_COLOR, fontSize: NAVIGATION_FONT_SIZE }}>
                                More
                            </div>
                            <div style={sty.iconDividerBlank} />
                        </div>
                    }
                </div>
            </div>

            <Drawer
                className="mobileNavigationDrawer"
                anchor="bottom"
                open={drawerOpen}
                onClose={() => setOpenDrawer(false)}
            >
                <MobileHeader
                    className="dashboardComponentMobileHeader"
                    onClickBrand={() => {
                        setOpenDrawer(false)
                    }}
                />
                <List>
                    {drawerNavigationItems.map((navItem: NavigationItem, index: number) => {
                        const isSelected = Boolean(currentNavigationItem && currentNavigationItem.name === navItem.name);

                        const additionalProps: any = {}

                        if (navItem.onClick) {
                            additionalProps['onClick'] = (e: any) => {
                                setOpenDrawer(false);
                                if (navItem.onClick) {
                                    navItem!.onClick(e)
                                }
                            }
                        } else {
                            additionalProps['component'] = Link
                            additionalProps['to'] = navItem.href!
                            additionalProps['onClick'] = () => { setOpenDrawer(false) }
                        }

                        return (
                            <ListItem
                                key={makeKey(navItem.name, index)}
                                className="mobileNavigationListItem"
                                button
                                {...additionalProps}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    padding: 16,
                                }}
                            >
                                <Icon
                                    className="mobileNavigationListItemIcon"
                                    icon={navItem.icon || ""} // NOTE: most likely dont need empty string
                                    color={isSelected ? Colors.purpleLight : GRAY_MENU_COLOR}
                                />
                                <div
                                    className="listItemTextElement"
                                    style={{ color: isSelected ? Colors.purpleLight : GRAY_MENU_COLOR }}
                                >
                                    {navItem.name}
                                </div>
                            </ListItem>
                        );
                    })}
                </List>
            </Drawer>

            {/* Toolkit Drawer - Same style as Settings */}
            <Drawer
                className="mobileNavigationToolkitDrawer"
                anchor="bottom"
                open={toolkitPopupOpen}
                onClose={() => setToolkitPopupOpen(false)}
            >
                <div className="toolkitDrawerContent" style={sty.toolkitDrawerContent}>
                    <div className="toolkitPopupHeader" style={sty.toolkitPopupHeader}>
                        <div className="toolkitPopupTitle" style={sty.toolkitPopupTitle}>
                            Marketing Tools
                        </div>
                    </div>
                    
                    <div className="toolkitPopupGrid" style={sty.toolkitPopupGrid}>
                        {navigationItems
                            .find((item: NavigationItem) => item.toolkitItems)
                            ?.toolkitItems?.map((toolkitItem: any, index: number) => (
                                <div
                                    key={makeKey('toolkit', toolkitItem.name, index)}
                                    className="toolkitPopupCard"
                                    style={{
                                        ...sty.toolkitPopupCard,
                                        backgroundColor: toolkitItem.color,
                                    }}
                                    onClick={() => {
                                        setToolkitPopupOpen(false)
                                        history.push(toolkitItem.href)
                                    }}
                                >
                                    <div className="toolkitPopupCardIcon" style={sty.toolkitPopupCardIcon}>
                                        <Icon
                                            className="toolkitPopupCardIconElement"
                                            icon={toolkitItem.icon}
                                            size={32}
                                            color={Colors.purple}
                                        />
                                    </div>
                                    <div className="toolkitPopupCardTitle" style={sty.toolkitPopupCardTitle}>
                                        {toolkitItem.name}
                                    </div>
                                    <div className="toolkitPopupCardDescription" style={sty.toolkitPopupCardDescription}>
                                        {toolkitItem.description}
                                    </div>
                                </div>
                            ))
                        }
                    </div>

                    {/* Quick Action Buttons - Moved to bottom for better thumb accessibility */}
                    {isBrandAdmin && (
                        <div className="toolkitQuickActions" style={sty.toolkitQuickActions}>
                            <ButtonSymphony
                                className="toolkitActionButtonNewWebsite"
                                size="small"
                                onClick={() => {
                                    setToolkitPopupOpen(false)
                                    setShowCreateNewWebsiteModalV2(true)
                                }}
                                iconLeft="plus"
                                style={sty.toolkitQuickActionButton}
                                iconLeftOffset={-8}
                                color="purpleLight"
                                iconLeftSize={18}
                            >
                                New Website
                            </ButtonSymphony>
                            <ButtonSymphony
                                className="toolkitActionButtonNewCampaign"
                                size="small"
                                onClick={() => {
                                    setToolkitPopupOpen(false)
                                    setShowCreateNewCampaignModal(true)
                                }}
                                iconLeft="plus"
                                style={sty.toolkitQuickActionButton}
                                iconLeftOffset={-8}
                                color="purple"
                                iconLeftSize={18}
                            >
                                New Campaign
                            </ButtonSymphony>
                            <ButtonSymphony
                                className="toolkitActionButtonNewPresave"
                                size="small"
                                onClick={() => {
                                    setToolkitPopupOpen(false)
                                    history.push('/marketing/new/presave')
                                }}
                                iconLeft="plus"
                                style={sty.toolkitQuickActionButton}
                                iconLeftOffset={-8}
                                color="green"
                                iconLeftSize={18}
                            >
                                New Presave
                            </ButtonSymphony>
                        </div>
                    )}
                </div>
            </Drawer>

            {/* Website Template Selection Modal */}
            <CreateNewWebsiteModalV2 />
        </div>
    )
}

export default MobileNavigation
